import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SearchRequest, SearchParameterType, PaginatedResponse, SearchFormData } from '../types';
import { apiService } from '../services/apiService';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { formatDateTime } from '../utils/dateFormatter';
import { SearchProgress } from '../components/SearchProgress';
import { useSearchUpdates } from '../hooks/useSearchUpdates';

interface SearchHistoryFilters {
  status: string;
  parameterType: string;
  search: string;
}

type SearchType = 'regular' | 'account_finder';

export const SearchPage: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchData, setSearchData] = useState<PaginatedResponse<SearchRequest> | null>(null);
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters] = useState<SearchHistoryFilters>({ status: '', parameterType: '', search: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>('regular');
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    parameterType: '',
    searchValue: ''
  });
  const [searchErrors, setSearchErrors] = useState<Record<string, string>>({});
  const [realTimeValidation, setRealTimeValidation] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // Add new state for tracking active searches
  const [activeSearches, setActiveSearches] = useState<Set<number>>(new Set());
  const [searchPolling, setSearchPolling] = useState<Map<number, NodeJS.Timeout>>(new Map());

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Loading search data with params:', {
        page: currentPage,
        page_size: pageSize,
        status: filters.status || undefined,
        parameter_type: filters.parameterType ? parseInt(filters.parameterType) : undefined,
        search: filters.search || undefined,
        ordering: '-created_at'
      });
      
      const [searchResponse, types] = await Promise.all([
        apiService.getSearchHistory({
          page: currentPage,
          page_size: pageSize,
          status: filters.status || undefined,
          parameter_type: filters.parameterType ? parseInt(filters.parameterType) : undefined,
          search: filters.search || undefined,
          ordering: '-created_at'
        }),
        apiService.getParameterTypes()
      ]);
      
      console.log('Search response received:', searchResponse);
      setSearchData(searchResponse);
      setParameterTypes(types);
    } catch (err) {
      console.error('Failed to load data:', err);
      showToast('Failed to load search history', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get selected parameter type for dynamic placeholders and validation
  const selectedParameterType = parameterTypes.find(t => t.id.toString() === searchForm.parameterType);
  const hasCountryCodes = selectedParameterType && Array.isArray(selectedParameterType.country_codes) && selectedParameterType.country_codes.length > 0;
  const [countryCode, setCountryCode] = useState<string>('');

  // Set default country code to India (+91) when phone number is selected
  useEffect(() => {
    if (selectedParameterType && selectedParameterType.name === 'phone_number' && hasCountryCodes) {
      // Find India's country code
      const indiaCode = selectedParameterType.country_codes?.find(cc => cc.country.includes('India'));
      if (indiaCode && !countryCode) {
        setCountryCode(indiaCode.code);
      }
    }
  }, [selectedParameterType, hasCountryCodes, countryCode]);

  // Get placeholder text based on selected parameter type
  const getPlaceholderText = (): string => {
    if (!selectedParameterType) return 'Enter search value...';
    
    // If country code is selected, show a more specific placeholder
    if (hasCountryCodes && countryCode) {
      return 'Enter phone number (10-11 digits)';
    }
    
    const placeholders: Record<string, string> = {
      'phone_number': '10-11 digit phone number',
      'vehicle_number': 'MH12AB1234',
      'ip_address': '192.168.1.1',
      'website': 'https://example.com',
      'email': 'user@example.com',
      'ifsc': 'SBIN0001234',
      'imei': '123456789012345',
      'company_id': 'A12345BC6789DEF123456',
      'director_id': '12345678',
      'voter_id': 'ABC1234567',
      'individual_pan': 'ABCDE1234F',
      'username': 'testuser123',
      'social_media_handle': '@username',
      'cryptocurrency_address': '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
      'upi': '9000000000@upi',
      'upi_id': '9000000000@upi'
    };
    // When display is "UPI" but backend name differs (e.g. "website"), show UPI example
    if (selectedParameterType.display_name?.toLowerCase() === 'upi') {
      return '9000000000@upi';
    }
    return placeholders[selectedParameterType.name] || 'Enter search value...';
  };

  // Fallback validation regexes (used when backend regex is wrong or missing). Based on standard specs.
  const FALLBACK_VALIDATION: Record<string, { regex: RegExp; message: string }> = {
    upi: {
      regex: /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z0-9._-]{2,64}$/,
      message: 'Enter a valid UPI ID (e.g. 9000000000@upi, name@paytm)'
    },
    upi_id: {
      regex: /^[a-zA-Z0-9._-]{2,256}@[a-zA-Z0-9._-]{2,64}$/,
      message: 'Enter a valid UPI ID (e.g. 9000000000@upi, name@paytm)'
    },
    email: {
      regex: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: 'Enter a valid email address'
    },
    phone_number: {
      regex: /^\+?[\d\s-]{10,15}$/,
      message: 'Enter a valid phone number'
    },
    ifsc: {
      regex: /^[A-Z]{4}0[A-Z0-9]{6}$/,
      message: 'Enter a valid IFSC code (e.g. SBIN0001234)'
    },
    vehicle_number: {
      regex: /^[A-Z]{2}[0-9]{2}[A-Z]{2}[0-9]{4}$/,
      message: 'Enter a valid vehicle number (e.g. DL01AB1234)'
    },
    individual_pan: {
      regex: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
      message: 'Enter a valid PAN (e.g. ABCDE1234F)'
    }
  };

  const getValidationForParamType = (paramType: SearchParameterType): { regex: RegExp; message: string } | null => {
    // Prefer fallback by name, or by display_name for UPI (backend may have wrong name e.g. "website")
    const fallback =
      FALLBACK_VALIDATION[paramType.name] ??
      (paramType.display_name?.toLowerCase() === 'upi' ? FALLBACK_VALIDATION.upi : undefined);
    if (fallback) return fallback;
    if (paramType.validation_regex) {
      try {
        return {
          regex: new RegExp(paramType.validation_regex),
          message: paramType.validation_error_message || 'Invalid format. Please check the example format.'
        };
      } catch {
        return fallback || null;
      }
    }
    return null;
  };

  const getEffectiveRegex = (): { regex: RegExp | null; message: string } | null => {
    if (!selectedParameterType) return null;
    return getValidationForParamType(selectedParameterType);
  };

  // Real-time validation with enhanced feedback
  const validateSearchValue = (value: string): string => {
    if (!selectedParameterType) {
      if (value.trim()) {
        return 'Please select a parameter type first';
      }
      return '';
    }
    
    const trimmedValue = value.trim();
    
    // If empty and parameter type is selected, show hint
    if (!trimmedValue) {
      return '';
    }
    
    // For phone numbers, require exactly 10 digits when country code is selected
    if (hasCountryCodes && countryCode && selectedParameterType.name === 'phone_number') {
      if (!/^\d{10}$/.test(trimmedValue)) {
        if (trimmedValue.length === 0) {
          return '';
        } else if (!/^\d+$/.test(trimmedValue)) {
          return 'Phone number must contain only digits';
        } else if (trimmedValue.length < 10) {
          return `Phone number must be 10 digits (${trimmedValue.length}/10)`;
        } else if (trimmedValue.length > 10) {
          return 'Phone number must be exactly 10 digits';
        }
        return 'Please enter a valid 10-digit phone number';
      }
      return '';
    }
    
    const effective = getEffectiveRegex();
    if (effective?.regex && !effective.regex.test(trimmedValue)) {
      return effective.message;
    }
    
    return '';
  };

  // Get input pattern restriction based on parameter type
  const getInputPattern = (): string | undefined => {
    if (!selectedParameterType) return undefined;
    
    const patterns: Record<string, string> = {
      'phone_number': '[0-9]*',
      'imei': '[0-9]*',
      'director_id': '[0-9]*',
      'ifsc': '[A-Z0-9]*',
      'vehicle_number': '[A-Z0-9]*',
      'company_id': '[A-Z0-9]*',
      'individual_pan': '[A-Z0-9]*',
      'voter_id': '[A-Z0-9]*'
    };
    
    return patterns[selectedParameterType.name];
  };

  // Get max length based on parameter type
  const getMaxLength = (): number | undefined => {
    if (!selectedParameterType) return undefined;
    
    const maxLengths: Record<string, number> = {
      'phone_number': 10,
      'imei': 15,
      'director_id': 8,
      'ifsc': 11,
      'vehicle_number': 10,
      'company_id': 21,
      'individual_pan': 10,
      'voter_id': 10
    };
    
    return maxLengths[selectedParameterType.name];
  };

  const validateSearchForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!searchForm.parameterType) {
      errors.parameterType = 'Please select a parameter type';
    }
    
    // Validate country code selection for parameter types that require it
    if (hasCountryCodes && !countryCode) {
      errors.countryCode = 'Please select a country code';
    }
    
    if (!searchForm.searchValue.trim()) {
      errors.searchValue = 'Please enter a search value';
    } else {
      // For phone numbers, require exactly 10 digits
      const selectedType = parameterTypes.find(t => t.id.toString() === searchForm.parameterType);
      if (hasCountryCodes && countryCode && selectedType && selectedType.name === 'phone_number') {
        if (!/^\d{10}$/.test(searchForm.searchValue.trim())) {
          errors.searchValue = 'Please enter a valid 10-digit phone number';
        }
      } else if (selectedType) {
        const validation = getValidationForParamType(selectedType);
        if (validation && !validation.regex.test(searchForm.searchValue.trim())) {
          errors.searchValue = validation.message;
        }
      }
    }
    
    setSearchErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Enhanced search submission
  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateSearchForm()) {
      return;
    }

    try {
      setIsSearching(true);
      
      // Prepare the search data
      let searchData: any = { ...searchForm };
      let cleanSearchValue = searchForm.searchValue.trim();
      
      if (hasCountryCodes && selectedParameterType?.name === 'phone_number') {
        // For phone numbers, ensure we extract country code if it's in the input
        // Remove country code from search value if user typed it
        if (cleanSearchValue.startsWith('+') || cleanSearchValue.match(/^\d{11,15}$/)) {
          // User might have entered full number with country code
          // Extract country code and clean the number
          const fullNumber = cleanSearchValue.replace(/[\s\-\(\)]/g, '');
          
          if (countryCode) {
            // If country code is selected, remove it from the input if present
            const codeToRemove = countryCode.startsWith('+') ? countryCode.slice(1) : countryCode;
            if (fullNumber.startsWith(codeToRemove)) {
              cleanSearchValue = fullNumber.slice(codeToRemove.length);
            } else {
              // If number doesn't start with selected country code, use as-is (might be different code)
              cleanSearchValue = fullNumber.replace(/^\+\d{1,3}/, '').replace(/^\d{1,3}/, '');
            }
          } else if (fullNumber.startsWith('+')) {
            // Extract country code from number starting with +
            const withoutPlus = fullNumber.slice(1);
            // Common country codes: India=91, US=1, UK=44, etc.
            const countryCodePatterns = ['91', '44', '1', '86', '81', '49', '33', '39', '34', '31', '46', '47', '45'];
            for (const code of countryCodePatterns) {
              if (withoutPlus.startsWith(code) && withoutPlus.length >= code.length + 7) {
                cleanSearchValue = withoutPlus.slice(code.length);
                searchData.countryCode = code;
                break;
              }
            }
            // If no pattern matched, try to extract first 1-3 digits as country code
            if (searchData.countryCode === undefined && withoutPlus.length >= 11) {
              const match = withoutPlus.match(/^(\d{1,3})(\d{7,15})$/);
              if (match) {
                searchData.countryCode = match[1];
                cleanSearchValue = match[2];
              }
            }
          }
        }
        
        // Ensure we only store the 10-digit phone number
        cleanSearchValue = cleanSearchValue.replace(/\D/g, '').slice(0, 10);
        searchData.searchValue = cleanSearchValue;
        
        // Use selected country code if we didn't extract one
        if (!searchData.countryCode && countryCode) {
          searchData.countryCode = countryCode;
        }
      } else if (hasCountryCodes && countryCode) {
        // For other parameter types that use country codes, concatenate them
        searchData.searchValue = countryCode + cleanSearchValue;
      } else {
        searchData.searchValue = cleanSearchValue;
      }
      
      // Create search request
      const searchRequest = await apiService.createSearch(searchData);
      
      // Show immediate success message
      showToast('Search initiated successfully! Processing in background...', 'success');
      
      // Add to active searches and start polling
      setActiveSearches(prev => new Set(prev).add(searchRequest.id));
      startPollingForResults(searchRequest.id);
      
      // Refresh the search history
      await loadData();
      
      // Clear the form
      setSearchForm({ parameterType: '', searchValue: '' });
      setCountryCode('');
      setSearchErrors({});
      setRealTimeValidation({});
      
    } catch (err) {
      console.error('Search failed:', err);
      let errorMessage = 'Search failed';
      
      if (err instanceof Error) {
        if (err.message.includes('Insufficient')) {
          errorMessage = 'Insufficient Int Coins. Please add more credits to continue.';
        } else if (err.message.includes('Invalid')) {
          errorMessage = 'Invalid search format. Please check your input and try again.';
        } else if (err.message.includes('Network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
          errorMessage = 'You do not have permission to perform this action.';
        } else {
          errorMessage = err.message;
        }
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // Polling function for real-time updates
  const startPollingForResults = (searchId: number) => {
    const pollInterval = setInterval(async () => {
      try {
        const searchRequest = await apiService.getSearchRequest(searchId);
        
        if (searchRequest.status === 'completed' || searchRequest.status === 'failed') {
          // Stop polling
          clearInterval(pollInterval);
          setSearchPolling(prev => {
            const newMap = new Map(prev);
            newMap.delete(searchId);
            return newMap;
          });
          setActiveSearches(prev => {
            const newSet = new Set(prev);
            newSet.delete(searchId);
            return newSet;
          });
          
          // Refresh the list
          await loadData();
          
          // Show completion message
          if (searchRequest.status === 'completed') {
            showToast('Search completed! View results in search history.', 'success');
          } else {
            showToast('Search failed. Please try again.', 'error');
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        clearInterval(pollInterval);
      }
    }, 3000); // Poll every 3 seconds
    
    // Store the interval for cleanup
    setSearchPolling(prev => new Map(prev).set(searchId, pollInterval));
    
    // Stop polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setActiveSearches(prev => {
        const newSet = new Set(prev);
        newSet.delete(searchId);
        return newSet;
      });
    }, 10 * 60 * 1000);
  };

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      searchPolling.forEach(interval => clearInterval(interval));
    };
  }, [searchPolling]);

  const handleSearchInputChange = (field: keyof SearchFormData, value: string) => {
    if (field === 'parameterType') {
      // Clear search value and validation when parameter type changes
      setSearchForm(prev => ({ 
        ...prev, 
        parameterType: value, 
        searchValue: '' // Clear the search value
      }));
      setSearchErrors(prev => ({ ...prev, searchValue: '', parameterType: '' }));
      setRealTimeValidation(prev => ({ ...prev, searchValue: '' }));
    } else {
      setSearchForm(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error when user starts typing
    if (searchErrors[field]) {
      setSearchErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Real-time validation for search value
    if (field === 'searchValue') {
      const validationError = validateSearchValue(value);
      setRealTimeValidation(prev => ({ ...prev, searchValue: validationError }));
    }
  };

  const handleDeactivateSearch = async (searchId: number) => {
    try {
      await apiService.deactivateSearchRequest(searchId);
      showToast('Search request deactivated successfully', 'success');
      await loadData(); // Refresh the search history
    } catch (err) {
      console.error('Failed to deactivate search:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate search';
      showToast(errorMessage, 'error');
    }
  };

  // Get parameter type icon
  const getParameterIcon = (parameterName: string): string => {
    const icons: Record<string, string> = {
      'phone_number': '📱',
      'vehicle_number': '🚗',
      'ip_address': '🌐',
      'website': '🌍',
      'email': '📧',
      'ifsc': '🏦',
      'imei': '📱',
      'company_id': '🏢',
      'director_id': '👤',
      'voter_id': '🗳️',
      'individual_pan': '🆔'
    };
    return icons[parameterName] || '🔍';
  };

  // Format phone number with country code for display
  const formatPhoneNumber = (countryCode: string, phoneNumber: string): string => {
    if (!countryCode) return phoneNumber;
    
    // Remove + prefix if present to avoid double plus
    const cleanCountryCode = countryCode.startsWith('+') ? countryCode.slice(1) : countryCode;
    return `+${cleanCountryCode} ${phoneNumber}`;
  };

  // Check if user has sufficient credits
  const hasInsufficientCredits = (user?.intCoinsBalance ?? 0) < 1;
  const hasLowBalance = (user?.intCoinsBalance ?? 0) < 5 && (user?.intCoinsBalance ?? 0) > 0;

  // --- SEARCH CARD ---
  const renderSearchCard = () => (
    <div className="mt-6">
      {/* 3D Scanner Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 sm:p-7 shadow-xl border border-indigo-500/20 mb-6">
        <div className="absolute right-0 top-0 bottom-0 w-full sm:w-2/5 opacity-25 sm:opacity-35 pointer-events-none overflow-hidden">
          <img 
            src="/images/3d/search-3d.jpg" 
            alt="3D OSINT Search Scanner" 
            className="h-full w-full object-cover object-center transform scale-105" 
          />
        </div>
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-2.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Active OSINT Intelligence Scanner
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1.5">
            Deep Forensic Query Engine
          </h2>
          <p className="text-xs sm:text-sm text-indigo-200/90 leading-relaxed">
            Multi-vector search across telecommunications, domains, vehicle registries, and public records with sub-second response times.
          </p>
        </div>
      </div>

      <Card className="w-full">
        {/* Header with Cost and Balance */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <span>Cost:</span>
            <span className="font-bold text-indigo-600 dark:text-indigo-400">1 Int Coin</span>
          </div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <span>Balance:</span>
            <span className={`font-bold ${hasInsufficientCredits ? 'text-red-600 dark:text-red-400' : hasLowBalance ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
              Int Coin: {user?.intCoinsBalance ?? 0}
            </span>
            <button 
              className={`text-xs underline ml-2 transition-colors ${
                hasInsufficientCredits 
                  ? 'text-red-600 dark:text-red-400 hover:text-red-800 font-semibold' 
                  : 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-800'
              }`}
              onClick={() => navigate('/app/subscriptions')}
            >
              {hasInsufficientCredits ? 'Add Credits Now' : 'Add More.'}
            </button>
          </div>
        </div>

        {/* Low Balance Warning */}
        {hasLowBalance && (
          <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Low balance! You have {user?.intCoinsBalance ?? 0} Int Coins remaining. Consider adding more credits.
              </p>
            </div>
          </div>
        )}

        {/* Insufficient Credits Warning */}
        {hasInsufficientCredits && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Insufficient Int Coins</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  You need at least 1 Int Coin to perform a search. Please add more credits to continue.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Search Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSearchSubmit} className="space-y-4">
              {/* Parameter Type Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Search Parameter Type *
                </label>
                <Select
                  value={searchForm.parameterType}
                  onChange={e => handleSearchInputChange('parameterType', e.target.value)}
                  className="w-full"
                  disabled={isSearching}
                >
                  <option value="">Select a parameter type</option>
                  {parameterTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {getParameterIcon(type.name)} {type.display_name}
                    </option>
                  ))}
                </Select>
                {searchErrors.parameterType && (
                  <p className="text-sm text-red-600 dark:text-red-400">{searchErrors.parameterType}</p>
                )}
              </div>

              {/* Quick Parameter Type Selector */}
              {parameterTypes.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                    Quick Select:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {parameterTypes
                      .filter(type => type.status === 'active')
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .slice(0, 6)
                      .map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => handleSearchInputChange('parameterType', type.id.toString())}
                        className={`px-3.5 py-1.5 text-xs font-medium rounded-full border transition-all duration-200 ${
                          searchForm.parameterType === type.id.toString()
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-transparent shadow-md shadow-indigo-500/25 scale-105'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/30'
                        }`}
                        disabled={isSearching}
                      >
                        {getParameterIcon(type.name)} {type.display_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Value Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Search Value *
                </label>
                {hasCountryCodes && (
                  <Select
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    className="w-full mb-2"
                    disabled={isSearching}
                  >
                    <option value="">Select country code</option>
                    {selectedParameterType.country_codes!.map(cc => (
                      <option key={cc.code} value={cc.code}>
                        {cc.flag} {cc.code} ({cc.country})
                      </option>
            ))}
          </Select>
                )}
                {hasCountryCodes && searchErrors.countryCode && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">{searchErrors.countryCode}</p>
                )}
          <Input
            type="text"
                  value={searchForm.searchValue}
                  onChange={e => {
                    let newValue = e.target.value;
                    // Apply input restrictions based on parameter type
                    if (selectedParameterType) {
                      const pattern = getInputPattern();
                      if (pattern === '[0-9]*' && !/^[0-9]*$/.test(newValue)) {
                        // Only allow digits
                        return;
                      } else if (pattern === '[A-Z0-9]*' && !/^[A-Z0-9]*$/i.test(newValue)) {
                        // Only allow alphanumeric (case-insensitive, we'll convert to uppercase)
                        newValue = newValue.toUpperCase();
                      }
                    }
                    handleSearchInputChange('searchValue', newValue);
                  }}
                  onBlur={e => {
                    const trimmedValue = e.target.value.trim();
                    if (trimmedValue !== searchForm.searchValue) {
                      handleSearchInputChange('searchValue', trimmedValue);
                    }
                    // Final validation on blur
                    const validationError = validateSearchValue(trimmedValue);
                    setRealTimeValidation(prev => ({ ...prev, searchValue: validationError }));
                    if (validationError) {
                      setSearchErrors(prev => ({ ...prev, searchValue: validationError }));
                    }
                  }}
                  onFocus={() => {
                    // Clear error when user focuses on input
                    if (searchErrors.searchValue && !realTimeValidation.searchValue) {
                      setSearchErrors(prev => ({ ...prev, searchValue: '' }));
                    }
                  }}
                  className={`w-full ${
                    realTimeValidation.searchValue || searchErrors.searchValue
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : searchForm.searchValue && selectedParameterType && !realTimeValidation.searchValue
                      ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                      : ''
                  }`}
                  placeholder={getPlaceholderText()}
                  disabled={isSearching || !selectedParameterType}
                  maxLength={getMaxLength()}
                  pattern={getInputPattern()}
                />
                {/* Show validation message based on priority: error > warning > success */}
                {searchErrors.searchValue ? (
                  <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {searchErrors.searchValue}
                  </p>
                ) : realTimeValidation.searchValue ? (
                  <p className="text-sm text-orange-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    {realTimeValidation.searchValue}
                  </p>
                ) : selectedParameterType && searchForm.searchValue && !realTimeValidation.searchValue ? (
                  <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Valid format
                  </p>
                ) : selectedParameterType && !searchForm.searchValue ? (
                  <p className="text-sm text-gray-500 italic">
                    Example: {getPlaceholderText()}
                  </p>
                ) : null}
              </div>

              {/* Search Button */}
              <div className="flex justify-center pt-2">
                <div className="relative group w-full sm:w-auto">
                  <Button 
                    type="submit" 
                    className="w-full sm:w-auto px-10 py-3 text-base font-semibold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25 active:scale-[0.98] transition-all"
                    disabled={isSearching || !searchForm.parameterType || !searchForm.searchValue.trim() || hasInsufficientCredits}
                  >
                    {isSearching ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Executing Intelligence Scan...
                      </span>
                    ) : hasInsufficientCredits ? (
                      'Insufficient Credits'
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Execute Deep Search
                      </span>
                    )}
                  </Button>
                  {hasInsufficientCredits && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      Add more Int Coins to perform searches
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </div>
              </div>
        </form>
            
            {/* Additional Options */}
            <div className="flex items-center gap-4 mt-4 text-xs text-gray-600 dark:text-gray-300">
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" className="accent-indigo-600" />
            View bulk report
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" className="accent-indigo-600" />
            Multiple Search
          </label>
            </div>
          </div>

          {/* Parameter Type Info Card */}
          <div className="lg:col-span-1">
            {selectedParameterType ? (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{getParameterIcon(selectedParameterType.name)}</span>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedParameterType.display_name}
                  </h3>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                  {selectedParameterType.description}
                </p>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Example:</span>
                    <p className="text-xs text-gray-600 dark:text-gray-300 font-mono">{getPlaceholderText()}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Format:</span>
                    <p className="text-xs text-gray-600 dark:text-gray-300 font-mono break-all">
                      {(getValidationForParamType(selectedParameterType)?.regex?.source) ?? selectedParameterType.validation_regex}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🔍</span>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Select Parameter Type
                  </h3>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Choose a parameter type to see its description and format requirements.
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );

  // --- SEARCH HISTORY TABLE ---
  const renderSearchHistoryTable = () => {
    const activeResults = searchData?.results?.filter(row => row.status !== 'inactive') || [];
    const hasResults = activeResults.length > 0;
    console.log('Rendering search history table:', {
      searchData,
      hasResults,
      resultsLength: searchData?.results?.length,
      activeResultsLength: activeResults.length,
      count: searchData?.count,
      currentPage,
      pageSize
    });
    return (
      <div className="mt-8">
        <Card className="w-full" title="Searched queries">
          
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500 dark:text-gray-400">Loading search history...</p>
            </div>
          ) : (
            <>
          <div className="overflow-x-auto custom-scrollbar border border-gray-200 dark:border-gray-750 rounded-xl">
            <table className="min-w-full text-sm text-gray-700 dark:text-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-750 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                <tr>
                  <th className="px-3.5 py-3 text-center">#</th>
                  <th className="px-3.5 py-3 text-center">Actions</th>
                  <th className="px-3.5 py-3 text-left">Query Target</th>
                  <th className="px-3.5 py-3 text-left">Status</th>
                  <th className="px-3.5 py-3 text-left">Timestamp</th>
                  <th className="px-3.5 py-3 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {hasResults ? activeResults.map((row, idx) => (
                  <tr key={row.id} className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/60 dark:bg-gray-850'} hover:bg-indigo-50/40 dark:hover:bg-gray-800 transition-colors`}>
                    <td className="px-3.5 py-3 text-center font-mono text-xs text-gray-500">{(currentPage - 1) * pageSize + idx + 1}</td>
                    <td className="px-3.5 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate(`/app/search/${row.id}/report`)}
                          disabled={row.status !== 'completed'}
                          title="View Intelligence Report"
                          className="hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h4m0 0V7a4 4 0 00-4-4H7a4 4 0 00-4 4v10a4 4 0 004 4h4" />
                          </svg>
                        </Button>
                      </div>
                    </td>
                    <td className="px-3.5 py-3 font-mono font-medium text-xs sm:text-sm text-gray-900 dark:text-gray-100 break-all">
                      {row.parameter_type_name === 'Phone Number' && row.country_code ? 
                        formatPhoneNumber(row.country_code, row.search_value) : 
                        row.search_value
                      }
                    </td>
                    <td className="px-3.5 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                          row.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60' :
                          row.status === 'processing' ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60' :
                          row.status === 'failed' ? 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/60' :
                          'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        }`}>
                          {row.status}
                        </span>
                        {row.status === 'processing' && activeSearches.has(row.id) && (
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-amber-500 border-t-transparent"></div>
                        )}
                      </div>
                    </td>
                    <td className="px-3.5 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                    <td className="px-3.5 py-3 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeactivateSearch(row.id)}
                        disabled={row.status === 'processing'}
                        className="hover:bg-rose-50 dark:hover:bg-rose-900/30 text-gray-400 hover:text-rose-600"
                        title="Remove from history"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">No searches recorded yet. Run your first query above!</td></tr>
                )}
              </tbody>
            </table>
          </div>
              
          {/* Pagination */}
          {searchData && searchData.count > pageSize && (
            <div className="flex flex-wrap items-center justify-center mt-6 gap-1.5 sm:gap-2">
              <Button size="sm" variant="outline" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
              <Button size="sm" variant="outline" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>Previous</Button>
              {Array.from({ length: Math.ceil((searchData?.count || 0) / pageSize) }, (_, i) => i).slice(Math.max(0, currentPage - 2), currentPage + 1).map(page => (
                <Button key={page + 1} size="sm" variant={currentPage === page + 1 ? 'default' : 'outline'} onClick={() => setCurrentPage(page + 1)}>{page + 1}</Button>
              ))}
              <Button size="sm" variant="outline" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === Math.ceil((searchData?.count || 0) / pageSize)}>Next</Button>
              <Button size="sm" variant="outline" onClick={() => setCurrentPage(Math.ceil((searchData?.count || 0) / pageSize))} disabled={currentPage === Math.ceil((searchData?.count || 0) / pageSize)}>Last</Button>
            </div>
          )}
            </>
          )}
        </Card>
      </div>
    );
  };

  // --- MAIN LAYOUT ---
  return (
    <>
      <PageHeader 
        title="Search" 
        subtitle="Run searches across supported parameters and view your recent history"
        align="center"
      />
      {renderSearchCard()}
      {renderSearchHistoryTable()}
    </>
  );
}; 